import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_ACCOUNT_LINK_FAILED,
  EVENT_ACCOUNT_LINK_HELP_EXPANDED,
  EVENT_ACCOUNT_LINK_PROMPT_DISMISSED,
  EVENT_ACCOUNT_LINK_PROMPT_SHOWN,
  EVENT_ACCOUNT_LINK_VERIFY_STARTED,
  EVENT_ACCOUNT_SETTINGS_OPENED,
  EVENT_CHANGE_PASSWORD_COMPLETED,
  EVENT_CHANGE_PASSWORD_STARTED,
  EVENT_DELETE_ACCOUNT_STARTED,
  EVENT_SIGN_IN_METHODS_VIEWED,
  EVENT_SIGN_IN_METHOD_BLOCKED,
  EVENT_SIGN_IN_METHOD_LINK_COMPLETED,
  EVENT_SIGN_IN_METHOD_LINK_STARTED,
  EVENT_SIGN_IN_METHOD_REMOVED,
  EVENT_SIGN_IN_PASSWORD_ADDED,
} from '@/lib/analyticsEvents';
import type { AccountSettingsOpenSource } from '@/lib/accountSettings';
import {
  inferPendingOAuthProvider,
  isAccountLinkRequiredError,
  isAuthMethodChangeError,
  type AccountLinkPromptEntry,
  type AuthMethodProvider,
} from '@/lib/accountLinking';

export type { AccountLinkPromptEntry };

export type AccountLinkSurface = 'collision_sheet' | 'account_settings';

export type AccountLinkVerifyMethod = 'email' | 'google' | 'apple';

export function resolvePromptEntry(
  pendingIdToken: string,
  storedEntry?: AccountLinkPromptEntry,
): AccountLinkPromptEntry {
  if (storedEntry) return storedEntry;
  return inferPendingOAuthProvider(pendingIdToken) ? 'oauth_callback' : 'email_sync';
}

export function accountLinkErrorCode(err: unknown): string | undefined {
  if (isAccountLinkRequiredError(err)) return err.code;
  if (isAuthMethodChangeError(err)) return err.code;
  if (err && typeof err === 'object' && 'code' in err && typeof (err as { code: unknown }).code === 'string') {
    return (err as { code: string }).code;
  }
  return undefined;
}

function promptPayload(
  existingMethods: AuthMethodProvider[],
  pendingIdToken: string,
  entry: AccountLinkPromptEntry,
): Record<string, unknown> {
  const pendingProvider = inferPendingOAuthProvider(pendingIdToken);
  return {
    existing_methods: existingMethods,
    entry,
    ...(pendingProvider ? { pending_provider: pendingProvider } : {}),
  };
}

export function trackAccountLinkPromptShown(
  existingMethods: AuthMethodProvider[],
  pendingIdToken: string,
  entry: AccountLinkPromptEntry,
): void {
  WebAnalytics.track(EVENT_ACCOUNT_LINK_PROMPT_SHOWN, promptPayload(existingMethods, pendingIdToken, entry));
}

export function trackAccountLinkPromptDismissed(
  existingMethods: AuthMethodProvider[],
  pendingIdToken: string,
  entry: AccountLinkPromptEntry,
  dismissMethod: 'not_now' | 'backdrop',
): void {
  WebAnalytics.track(EVENT_ACCOUNT_LINK_PROMPT_DISMISSED, {
    ...promptPayload(existingMethods, pendingIdToken, entry),
    dismiss_method: dismissMethod,
    source: 'collision_sheet' satisfies AccountLinkSurface,
  });
}

export function trackAccountLinkVerifyStarted(
  verifyMethod: AccountLinkVerifyMethod,
  existingMethods: AuthMethodProvider[],
  pendingIdToken: string,
  entry: AccountLinkPromptEntry,
): void {
  const pendingProvider = inferPendingOAuthProvider(pendingIdToken);
  WebAnalytics.track(EVENT_ACCOUNT_LINK_VERIFY_STARTED, {
    verify_method: verifyMethod,
    existing_methods: existingMethods,
    entry,
    source: 'collision_sheet' satisfies AccountLinkSurface,
    ...(pendingProvider ? { pending_provider: pendingProvider } : {}),
  });
}

export function trackAccountLinkFailed(
  verifyMethod: AccountLinkVerifyMethod,
  existingMethods: AuthMethodProvider[],
  entry: AccountLinkPromptEntry,
  errorCode?: string,
): void {
  WebAnalytics.track(EVENT_ACCOUNT_LINK_FAILED, {
    verify_method: verifyMethod,
    existing_methods: existingMethods,
    entry,
    source: 'collision_sheet' satisfies AccountLinkSurface,
    ...(errorCode ? { error_code: errorCode } : {}),
  });
}

export function trackAccountLinkHelpExpanded(
  existingMethods: AuthMethodProvider[],
  pendingIdToken: string,
): void {
  const pendingProvider = inferPendingOAuthProvider(pendingIdToken);
  WebAnalytics.track(EVENT_ACCOUNT_LINK_HELP_EXPANDED, {
    existing_methods: existingMethods,
    ...(pendingProvider ? { pending_provider: pendingProvider } : {}),
  });
}

export function trackSignInMethodsViewed(methods: AuthMethodProvider[]): void {
  WebAnalytics.track(EVENT_SIGN_IN_METHODS_VIEWED, {
    methods,
    method_count: methods.length,
    source: 'account_settings' satisfies AccountLinkSurface,
  });
}

export function trackSignInMethodLinkStarted(provider: 'google' | 'apple'): void {
  WebAnalytics.track(EVENT_SIGN_IN_METHOD_LINK_STARTED, {
    provider,
    source: 'account_settings' satisfies AccountLinkSurface,
  });
}

export function trackSignInMethodLinkCompleted(provider: 'google' | 'apple'): void {
  WebAnalytics.track(EVENT_SIGN_IN_METHOD_LINK_COMPLETED, {
    provider,
    source: 'account_settings' satisfies AccountLinkSurface,
  });
}

export function trackSignInMethodLinkFailed(provider: 'google' | 'apple', errorCode?: string): void {
  WebAnalytics.track(EVENT_ACCOUNT_LINK_FAILED, {
    verify_method: provider,
    source: 'account_settings' satisfies AccountLinkSurface,
    ...(errorCode ? { error_code: errorCode } : {}),
  });
}

export function trackSignInMethodRemoved(
  provider: 'google' | 'apple',
  methodsAfter: AuthMethodProvider[],
): void {
  WebAnalytics.track(EVENT_SIGN_IN_METHOD_REMOVED, {
    provider,
    methods_after: methodsAfter,
    source: 'account_settings' satisfies AccountLinkSurface,
  });
}

export function trackSignInPasswordAdded(): void {
  WebAnalytics.track(EVENT_SIGN_IN_PASSWORD_ADDED, {
    source: 'account_settings' satisfies AccountLinkSurface,
  });
}

export function trackSignInMethodBlocked(
  code: 'LAST_METHOD' | 'PASSWORD_REQUIRED',
  provider?: 'google' | 'apple',
): void {
  WebAnalytics.track(EVENT_SIGN_IN_METHOD_BLOCKED, {
    code,
    source: 'account_settings' satisfies AccountLinkSurface,
    ...(provider ? { provider } : {}),
  });
}

export function trackAccountSettingsOpened(source: AccountSettingsOpenSource): void {
  WebAnalytics.track(EVENT_ACCOUNT_SETTINGS_OPENED, { source });
}

export function trackChangePasswordStarted(): void {
  WebAnalytics.track(EVENT_CHANGE_PASSWORD_STARTED, {
    source: 'account_settings' satisfies AccountLinkSurface,
  });
}

export function trackChangePasswordCompleted(): void {
  WebAnalytics.track(EVENT_CHANGE_PASSWORD_COMPLETED, {
    source: 'account_settings' satisfies AccountLinkSurface,
  });
}

export function trackDeleteAccountStarted(): void {
  WebAnalytics.track(EVENT_DELETE_ACCOUNT_STARTED, {
    source: 'account_settings' satisfies AccountLinkSurface,
  });
}
